import { yupResolver } from '@hookform/resolvers/yup';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Switch,
  FormControl,
  FormLabel,
  FormErrorMessage,
  HStack,
  Textarea,
  SimpleGrid,
  CurrencyInput,
} from '@wegroup/design-system';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import * as yup from 'yup';
import { DateInputV4 as DateInput } from 'wg-fe-ui';
import { format } from 'date-fns';
import { Claim } from '../../../../types/CompanyScanTypes';
import LinksInput from '../links/LinksInput';
import { SmtFile } from '../../../../../../../types/Form';

interface Props {
  claim?: Claim;
  isOpen: boolean;
  onClose: () => void;
  addClaim: (claim: Claim) => void;
  editClaim: (claim: Claim) => void;
}

const claimValidationSchema = yup.object().shape({
  circumstances: yup.string().required('Required'),
  claim_date: yup.string().required('Required'),
  expenses: yup
    .number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .required('Required'),
  has_certificate: yup.boolean(),
  documents: yup.array(),
});

const NewClaimModal: React.FC<React.PropsWithChildren<Props>> = ({
  claim,
  addClaim,
  editClaim,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();

  const modalGoal = claim ? 'EDIT' : 'ADD';

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<Claim>({
    resolver: yupResolver(claimValidationSchema),
    defaultValues: {
      circumstances: claim?.circumstances,
      claim_date: claim?.claim_date,
      expenses: claim?.expenses,
      has_certificate: claim?.has_certificate || false,
      documents: claim?.documents,
    },
  });

  const handleOnClose = () => {
    reset();
    onClose();
  };

  const handleOnSubmit = handleSubmit((newClaim: Claim) => {
    if (claim)
      editClaim({
        ...newClaim,
        claim_date: format(
          new Date(newClaim.claim_date),
          `yyyy-MM-dd'T'HH:mm:ss.SSSxxx`,
        ),
      });
    else
      addClaim({
        ...newClaim,
        claim_date: format(
          new Date(newClaim.claim_date),
          `yyyy-MM-dd'T'HH:mm:ss.SSSxxx`,
        ),
      });
    handleOnClose();
  });

  return (
    <Modal isOpen={isOpen} onClose={handleOnClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t(`FLOWS_COMPANY-SCAN_CLAIMS_${modalGoal}`)}</ModalHeader>
        <ModalCloseButton />
        <ModalBody as={SimpleGrid} columns={2} gap="4">
          <FormControl isInvalid={!!errors.circumstances} gridColumn="span 2">
            <FormLabel>{t('CIRCUMSTANCES')}</FormLabel>

            <Textarea {...register('circumstances')} />
            {errors.circumstances?.message && (
              <FormErrorMessage>
                {t(errors.circumstances.message)}
              </FormErrorMessage>
            )}
          </FormControl>

          <FormControl isInvalid={!!errors.claim_date} alignSelf="flex-end">
            <FormLabel>{t('Date of the claim')}</FormLabel>
            <Controller
              name="claim_date"
              control={control}
              render={({ field: { onChange, value } }) => (
                <StyledDateInput
                  error={t(errors.claim_date?.message || '')}
                  onChange={({ value }: { value: Date }) => onChange(value)}
                  value={value}
                />
              )}
            />
          </FormControl>

          <FormControl isInvalid={!!errors.expenses}>
            <FormLabel>{t('FLOWS_COMPANY-SCAN_CLAIMS_EXPENSES')}</FormLabel>
            <Controller
              name="expenses"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CurrencyInput
                  onChange={({ value }) => onChange(value)}
                  value={value}
                />
              )}
            />

            {errors.expenses?.message && (
              <FormErrorMessage>{t(errors.expenses.message)}</FormErrorMessage>
            )}
          </FormControl>

          <FormControl isInvalid={!!errors.has_certificate}>
            <FormLabel>{t('HAS_CERTIFICATE')}</FormLabel>
            <Controller
              name="has_certificate"
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <Switch
                  name={name}
                  isChecked={value}
                  onChange={onChange}
                  checked={value}
                />
              )}
            />
            <FormErrorMessage>
              {errors.has_certificate?.message}
            </FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.documents} gridColumn="span 2">
            <FormLabel>{t('DOCUMENTS')}</FormLabel>
            <Controller
              name="documents"
              control={control}
              render={({ field: { onChange, value } }) => (
                <LinksInput
                  value={value as SmtFile[]}
                  onChange={(files) => onChange(files)}
                />
              )}
            />
          </FormControl>
        </ModalBody>

        <ModalFooter as={HStack} justify="flex-end" spacing="4">
          <Button variant="ghost" mr={3} onClick={handleOnClose}>
            {t('CLOSE')}
          </Button>
          <Button onClick={handleOnSubmit}>
            {modalGoal ? t('SAVE') : t('ADD')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const StyledDateInput = styled(DateInput)`
  height: unset;
  > div {
    /* ErrorContainer */
    height: unset;
  }
`;

export default NewClaimModal;
